// Shared schema.org structured data used across pages.
// Keep product facts (price, availability, specs) consistent with the
// FAQ content in src/components/FAQSection.js.

export const SITE_URL = "https://www.the-brik.com";

export const PRODUCT_IMAGE = `${SITE_URL}/images/metal-wallet-with-key-holder-and-tracking-card.jpg`;

// Official profiles. Used as sameAs links so Google connects the brand
// entity "The Brik Wallet" to its social presence.
export const SOCIAL_PROFILES = [
  "https://www.instagram.com/the.brik/",
  "https://www.tiktok.com/@thebrik",
  "https://www.facebook.com/people/The-Brik/61574215883635/",
  "https://www.linkedin.com/company/103414107",
];

export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "The Brik",
  alternateName: "The Brik Wallet",
  url: SITE_URL,
  logo: `${SITE_URL}/BRIK_LOGO.png`,
  description:
    "The Brik Wallet is a metal wallet that keeps your cards, ID, cash, keys, and a rechargeable tracking card together in one compact everyday carry.",
  sameAs: SOCIAL_PROFILES,
  founder: {
    "@type": "Person",
    name: "Asray Gopa",
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "119 Stanton Avenue",
    addressLocality: "Ames",
    addressRegion: "IA",
    postalCode: "50014",
    addressCountry: "US",
  },
  contactPoint: {
    "@type": "ContactPoint",
    email: "asray@the-brik.com",
    telephone: "+1-714-787-9947",
    contactType: "customer support",
  },
};

export const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "The Brik",
  alternateName: "The Brik Wallet",
  url: SITE_URL,
};

// Shared aggregate rating for the one canonical Product entity. Derived from
// 20 collected metal-Brik customer reviews (16 five-star, 4 four-star = 4.8),
// the same reviews displayed on /metal-wallet and mirrored in PRODUCT_REVIEWS
// below. Update this AND the on-page REVIEWS in MetalWallet.js together.
export const productAggregateRating = {
  "@type": "AggregateRating",
  ratingValue: "4.8",
  reviewCount: "20",
  bestRating: "5",
};

// The individual reviews behind productAggregateRating. These mirror the real
// buyer reviews shown on /metal-wallet (the REVIEWS array in MetalWallet.js);
// keep the two lists in sync. Attached to the Product entity so Search has the
// "review" field it expects alongside "aggregateRating" and "offers".
export const productReviews = [
  { rating: 5, text: "I like having my keys, cards, cash, and tracker all in one place instead of checking three different pockets." },
  { rating: 5, text: "It took me a day to get used to, but now I don't leave the house without it." },
  { rating: 5, text: "The ID window is surprisingly convenient, especially at bars and when I'm traveling." },
  { rating: 4, text: "It's a little heavier than my old wallet, but it replaces my wallet and keychain, so it's worth it." },
  { rating: 5, text: "I bought it because I constantly lose my keys, and having everything attached has actually helped." },
  { rating: 5, text: "The Brik feels sturdy and way more useful than the minimalist wallets I've tried before." },
  { rating: 4, text: "The card section was tight at first, but after using it for a week, it broke in nicely." },
  { rating: 5, text: "My favorite part is that I can see my ID without pulling everything out." },
  { rating: 5, text: "It looks clean, holds everything I need, and has become part of my everyday routine." },
  { rating: 5, text: "I used to do the phone-wallet-keys pocket check every morning. Now it's basically just phone and Brik." },
  { rating: 4, text: "Definitely different from a normal wallet, but the key holder and tracker make it much more practical." },
  { rating: 5, text: "I got it for college because I kept misplacing my dorm keys. This would have saved me a lot of stress freshman year." },
  { rating: 5, text: "It's compact without forcing me to give up cash, keys, or the cards I actually use." },
  { rating: 5, text: "The build quality feels solid, and I've already had a few people ask me what it is." },
  { rating: 5, text: "It sounds simple, but knowing my wallet and keys are always together makes leaving the house easier." },
  { rating: 4, text: "I wish there were more color options, but the actual design is clever and useful." },
  { rating: 5, text: "The tracker slot gives me a lot more peace of mind when I'm traveling." },
  { rating: 5, text: "This is one of those products that seems unusual until you use it, and then a regular wallet feels incomplete." },
  { rating: 5, text: "I bought it as a gift for my son in college, and he uses it every day." },
  { rating: 5, text: "No more loose keys scratching my phone or falling to the bottom of my pocket." },
].map((r) => ({
  "@type": "Review",
  reviewRating: {
    "@type": "Rating",
    ratingValue: String(r.rating),
    bestRating: "5",
  },
  author: { "@type": "Person", name: "Verified Buyer" },
  reviewBody: r.text,
}));

// The one canonical Product entity. Full Product + Offer markup belongs ONLY
// on /metal-wallet (the page where the product is the main entity and can be
// bought). Other pages must use metalWalletProductRefJsonLd below.
export const metalWalletProductJsonLd = {
  "@context": "https://schema.org/",
  "@type": "Product",
  "@id": `${SITE_URL}/metal-wallet#product`,
  name: "The Metal Brik Wallet",
  description:
    "Metal wallet with an integrated key holder and rechargeable tracking card. Holds 1 ID up front, 7-8 cards in the RFID-protected main compartment, and cash in the back. Machined from anodized aluminum.",
  brand: { "@type": "Brand", name: "The Brik" },
  image: PRODUCT_IMAGE,
  sku: "METAL-WALLET",
  material: "Anodized aluminum",
  weight: {
    "@type": "QuantitativeValue",
    value: "34",
    unitCode: "GRM",
  },
  height: { "@type": "QuantitativeValue", value: "8.5", unitCode: "CMT" },
  width: { "@type": "QuantitativeValue", value: "5.5", unitCode: "CMT" },
  depth: { "@type": "QuantitativeValue", value: "1.5", unitCode: "CMT" },
  url: `${SITE_URL}/metal-wallet`,
  offers: {
    "@type": "Offer",
    url: `${SITE_URL}/metal-wallet`,
    priceCurrency: "USD",
    price: "69.99",
    availability: "https://schema.org/InStock",
    itemCondition: "https://schema.org/NewCondition",
    seller: {
      "@type": "Organization",
      name: "The Brik",
    },
    // Shipping is free on every order.
    shippingDetails: {
      "@type": "OfferShippingDetails",
      shippingRate: {
        "@type": "MonetaryAmount",
        value: "0.00",
        currency: "USD",
      },
      shippingDestination: {
        "@type": "DefinedRegion",
        addressCountry: "US",
      },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: {
          "@type": "QuantitativeValue",
          minValue: 0,
          maxValue: 1,
          unitCode: "DAY",
        },
        transitTime: {
          "@type": "QuantitativeValue",
          minValue: 5,
          maxValue: 7,
          unitCode: "DAY",
        },
      },
    },
    // The published policy at /exchange-and-delivery is exchange-only
    // ("we do not offer refunds"). MerchantReturnNotPermitted is the honest
    // schema.org expression of that. Switch to
    // MerchantReturnFiniteReturnWindow only if a money-back window is ever
    // offered.
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy",
      applicableCountry: "US",
      returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
    },
  },
  // aggregateRating + review are the 20 real metal-Brik reviews displayed on
  // /metal-wallet, which is what makes this markup legitimate. Both come from
  // the shared constants above; update those together with the on-page REVIEWS
  // in MetalWallet.js as new reviews come in.
  aggregateRating: productAggregateRating,
  review: productReviews,
};

// Mention-level reference for pages where the wallet is discussed but is not
// the main purchasable entity (Home, landing pages). Consolidates to the same
// entity via @id without duplicating the Offer, which stays exclusive to the
// canonical Product on /metal-wallet. It DOES carry aggregateRating + review:
// those are intrinsic properties of the product entity (the same @id), and
// including them keeps every page's Product valid for Search's Product-snippet
// requirement of "offers, review, or aggregateRating".
export const metalWalletProductRefJsonLd = {
  "@context": "https://schema.org/",
  "@type": "Product",
  "@id": `${SITE_URL}/metal-wallet#product`,
  name: "The Metal Brik Wallet",
  url: `${SITE_URL}/metal-wallet`,
  image: PRODUCT_IMAGE,
  brand: { "@type": "Brand", name: "The Brik" },
  aggregateRating: productAggregateRating,
  review: productReviews,
};

export const makeBreadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const makeVideoJsonLd = ({ name, description, poster, src, duration }) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name,
  description,
  thumbnailUrl: `${SITE_URL}${poster}`,
  contentUrl: `${SITE_URL}${src}`,
  uploadDate: "2026-07-16T12:00:00-05:00",
  duration,
});

export const makeFaqJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  })),
});
